import React, { memo, useState, useMemo } from 'react';
import { SmartTaggedMemberCard } from './SmartTaggedMemberCard';
import { Member } from '@/types/member';
import { UserStatus } from '@/types/presence';

interface SmartTaggedMemberCardMemoProps {
  member: Member;
  viewMode: 'grid' | 'list';
  currentUserId?: string;
  onViewProfile: (member: Member) => void;
  onMessage: (member: Member) => void;
  onConnect: (member: Member) => void;
  onAccept?: (member: Member) => void;
  onDecline?: (member: Member) => void;
  onFavorite: (member: Member) => void;
  onShare: (member: Member) => void;
  userStatus?: UserStatus;
  compatibilityScore?: number;
  mutualConnections?: number;
  lastActive?: string;
  searchTerm?: string;
  allMembers: Member[];
  connectionStatus?: string | null;
}

// Memoized comparison function
const membersAreEqual = (prev: SmartTaggedMemberCardMemoProps, next: SmartTaggedMemberCardMemoProps) => {
  // Check member core properties
  if (
    prev.member.id !== next.member.id ||
    prev.member.name !== next.member.name ||
    prev.member.image !== next.member.image ||
    prev.member.role !== next.member.role ||
    prev.member.company !== next.member.company ||
    prev.viewMode !== next.viewMode ||
    prev.connectionStatus !== next.connectionStatus ||
    prev.searchTerm !== next.searchTerm
  ) {
    return false;
  }

  // Check optional props
  if (
    prev.userStatus !== next.userStatus ||
    prev.compatibilityScore !== next.compatibilityScore ||
    prev.mutualConnections !== next.mutualConnections ||
    prev.lastActive !== next.lastActive
  ) {
    return false;
  }

  return true;
};

export const SmartTaggedMemberCardMemo = memo<SmartTaggedMemberCardMemoProps>((props) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Only generate expensive computations when hovered or in view
  const shouldLoadContext = useMemo(() => {
    return isHovered || props.viewMode === 'list' || Boolean(props.searchTerm);
  }, [isHovered, props.viewMode, props.searchTerm]);

  return (
    <div 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SmartTaggedMemberCard
        {...props}
      />
    </div>
  );
}, membersAreEqual);

SmartTaggedMemberCardMemo.displayName = 'SmartTaggedMemberCardMemo';